import { Trans } from "@web-monorepo/i18n/macro";
import type { TranslatedString } from "@web-monorepo/translation-runtime";
import React from "react";
import type { AppPageVariant } from "src/shared/components/AppPage";
import styles from "./EditableSetup.module.css";
import SavedToolNewButton from "./SavedToolNewButton";

export type ToolListItem = {
  text: string;
};

export type ToolPayload = {
  title?: string;
  text?: string;
  list?: ToolListItem[];
};

export type Tool = {
  _id?: string;
  dojoId?: string;
  type?: string;
  payload: ToolPayload;

  // FIXME: this is necessary because of some weird logic when rendering DirectionsSenderDisplay
  // the renderResult function receives a tool that has these props due to some
  // redux state inconsistencies.
  list?: ToolListItem[];
  title?: string;
  text?: string;
};

export type AppMetadata = {
  href: string;
  title: TranslatedString;
  /**
   * AppPage / PresentationPage / SavedToolNewButton variant for this pod's
   * branded chrome. Spread via `{...appMetadata}` so every pod must declare
   * its own variant.
   */
  variant: AppPageVariant;
  icon?: string;
};

type EditableSetupProps = {
  appMetadata: AppMetadata;
  title?: string;
  renderItem: ({
    tool,
    openEditScreen,
    showTool,
  }: {
    tool: Tool;
    openEditScreen: (tool: Tool) => void;
    showTool: (tool: Tool) => void;
  }) => React.ReactElement;
  openEditScreen: (tool?: Tool) => void;
  showTool: (tool: Tool) => void;
  tools: Tool[];
};

export default function EditableSetup({
  appMetadata,
  title,
  renderItem,
  openEditScreen,
  showTool,
  tools,
}: EditableSetupProps) {
  return (
    <article className={styles.app}>
      <header>{title ? <h2 className={styles.intro}>{title}</h2> : null}</header>
      <section>
        <ul className={styles.list}>
          {/*
           * Wrap so the React SyntheticEvent isn't forwarded as the `tool`
           * argument — the pre-VM class component had the same latent bug,
           * but Valtio's deep-snapshot traversal hits cross-origin frame refs
           * (event.view → Window) and throws on the next render.
           */}
          <SavedToolNewButton onClick={() => openEditScreen()} variant={appMetadata.variant}>
            <Trans>New</Trans>
          </SavedToolNewButton>
          {tools
            ?.map(({ payload, _id, dojoId }) => ({ ...payload, payload, _id, dojoId }))
            .map((tool) => renderItem({ tool, openEditScreen, showTool }))}
        </ul>
      </section>
    </article>
  );
}
